import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress
} from '@mui/material';
import { Plus, Minus, Download, Calendar, RefreshCw, ArrowLeft } from 'lucide-react';
import { ExpandMore, FilterList, PictureAsPdf, Microsoft, EmailTwoTone, Save, CheckCircle, Error as ErrorIcon, CloudSync, Person, FlightTakeoff, Hotel, Groups } from '@mui/icons-material';
import { format, addDays, parse } from 'date-fns';
import axiosInstance from '../../utils/axiosConfig';
import { 
  EVENT_TYPES,
  RESIDENCY_EVENT_TYPES,
  ScheduleItem,
  getCoolerTemplate,
  getDefaultVenueTemplate,
  getInternationalTemplate,
  getNavigatorResidencyTemplate,
  getSmartInternationalSchedule
} from '../../utils/scheduleTemplates';
import { exportScheduleToPDF } from '../../utils/pdfExport';
import { exportScheduleToWord } from '../../utils/wordExport';
import EmailScheduleDialog from './EmailScheduleDialog';
import TourVariablesSection from '../Schedule/TourVariablesSection';
import { getTourTemplate, getTourEventTypes } from '../../utils/tourTemplates';
import { TourVariables } from '../../types/Tour';

// Union type for all possible variable types
type ScheduleVariables = TemplateVariables | TourVariables;

// Type guard to check if variables are TourVariables
const isTourVariables = (variables: ScheduleVariables, scheduleType: string): variables is TourVariables => {
  return scheduleType === 'tour';
};

// Create a complete TourVariables object with proper defaults
const createDefaultTourVariables = (performanceData: any, performanceDate: Date): TourVariables => {
  return {
    // Basic fields required by backend validation
    ARTIST: performanceData?.bandName || '',
    VENUE: performanceData?.venue?.name || '',
    VENUE_ADDRESS: performanceData?.venue?.location || '',
    DATE: format(performanceDate, 'dd-MM-yyyy'),
    CONTACT_NAME: performanceData?.contactName || '',
    CONTACT_EMAIL: performanceData?.contactEmail || '',
    CONTACT_PHONE: performanceData?.contactPhone || '',
    
    // Tour Overview
    TOUR_NAME: '',
    TOUR_DURATION: '7',
    NUMBER_OF_SHOWS: '3',
    TOUR_START_DATE: format(performanceDate, 'yyyy-MM-dd'),
    TOUR_END_DATE: format(addDays(performanceDate, 7), 'yyyy-MM-dd'),
    
    // Tour Team
    HAS_TOUR_MANAGER: false,
    TOUR_MANAGER: '',
    TOUR_MANAGER_PHONE: '',
    TOUR_MANAGER_EMAIL: '',
    
    HAS_PRODUCTION_MANAGER: false,
    PRODUCTION_MANAGER: '',
    PRODUCTION_MANAGER_PHONE: '',
    PRODUCTION_MANAGER_EMAIL: '',
    
    // Global Tour Settings
    TRANSPORT_TYPE: 'private_car' as any,
    ACCOMMODATION_STANDARD: 'standard' as any,
    HOTEL_CHAIN_PREFERENCE: '',
    TOUR_DAILY_ALLOWANCE: '30',
    
    // Performance Standards
    DEFAULT_SOUNDCHECK_DURATION: '60',
    DEFAULT_PERFORMANCE_DURATION: '90',
    DEFAULT_LOAD_IN_DURATION: '120',
    DEFAULT_LOAD_OUT_DURATION: '60',
    
    // Travel Settings
    TRAVEL_BUFFER_TIME: '2',
    DEFAULT_CHECK_IN_TIME: '15:00',
    DEFAULT_CHECK_OUT_TIME: '11:00',
    
    // Store UI flags
    UI_FLAGS: {
      hasTourManager: false,
      hasProductionManager: false,
      numberOfShows: 3,
      venueFlags: {}
    },
    
    // Venue 1 (populated from performance data)
    VENUE_1_NAME: performanceData?.venue?.name || '',
    VENUE_1_ADDRESS: performanceData?.venue?.location || '',
    VENUE_1_CITY: 'Dublin', // Default, user can change
    VENUE_1_PERFORMANCE_DATE: format(performanceDate, 'yyyy-MM-dd'),
    VENUE_1_PERFORMANCE_TIME: '20:00',
    VENUE_1_SOUNDCHECK_TIME: '17:00',
    VENUE_1_PRIMARY_CONTACT_NAME: performanceData?.contactName || '',
    VENUE_1_PRIMARY_CONTACT_EMAIL: performanceData?.contactEmail || '',
    VENUE_1_PRIMARY_CONTACT_PHONE: performanceData?.contactPhone || '',
    VENUE_1_TECH_CONTACT_NAME: '',
    VENUE_1_TECH_CONTACT_EMAIL: '',
    VENUE_1_TECH_CONTACT_PHONE: '',
    VENUE_1_ARTIST_LIAISON_NAME: '',
    VENUE_1_ARTIST_LIAISON_EMAIL: '',
    VENUE_1_ARTIST_LIAISON_PHONE: '',
    VENUE_1_TRANSPORT_CONTACT_NAME: '',
    VENUE_1_TRANSPORT_CONTACT_PHONE: '',
    VENUE_1_HOTEL_CONTACT_NAME: '',
    VENUE_1_HOTEL_CONTACT_PHONE: '',
    VENUE_1_CATERING_CONTACT_NAME: '',
    VENUE_1_CATERING_CONTACT_PHONE: '',
    VENUE_1_SECURITY_CONTACT_NAME: '',
    VENUE_1_SECURITY_CONTACT_PHONE: '',
    VENUE_1_MERCHANDISING_CONTACT_NAME: '',
    VENUE_1_MERCHANDISING_CONTACT_PHONE: '',
    VENUE_1_PROMOTER_CONTACT_NAME: '',
    VENUE_1_PROMOTER_CONTACT_EMAIL: '',
    VENUE_1_PROMOTER_CONTACT_PHONE: '',
    VENUE_1_PRODUCTION_CONTACT_NAME: '',
    VENUE_1_PRODUCTION_CONTACT_PHONE: '',
    VENUE_1_MARKETING_CONTACT_NAME: '',
    VENUE_1_MARKETING_CONTACT_EMAIL: '',
    VENUE_1_LUNCH_TYPE: 'venue_catering' as any,
    VENUE_1_LUNCH_LOCATION: '',
    VENUE_1_LUNCH_BUDGET: '',
    VENUE_1_DINNER_TYPE: 'venue_catering' as any,
    VENUE_1_DINNER_LOCATION: '',
    VENUE_1_HOTEL_NAME: 'Dublin Hotel',
    VENUE_1_HOTEL_ADDRESS: '',
    
    // Initialize all venues 2-14 with empty defaults
    ...Object.fromEntries(
      Array.from({ length: 13 }, (_, i) => {
        const venueNum = i + 2;
        const baseDate = addDays(performanceDate, i + 1);
        return [
          [`VENUE_${venueNum}_NAME`, ''],
          [`VENUE_${venueNum}_ADDRESS`, ''],
          [`VENUE_${venueNum}_CITY`, ''],
          [`VENUE_${venueNum}_PERFORMANCE_DATE`, format(baseDate, 'yyyy-MM-dd')],
          [`VENUE_${venueNum}_PERFORMANCE_TIME`, '20:00'],
          [`VENUE_${venueNum}_SOUNDCHECK_TIME`, '17:00'],
          [`VENUE_${venueNum}_PRIMARY_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_PRIMARY_CONTACT_EMAIL`, ''],
          [`VENUE_${venueNum}_PRIMARY_CONTACT_PHONE`, ''],
          [`VENUE_${venueNum}_TECH_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_TECH_CONTACT_EMAIL`, ''],
          [`VENUE_${venueNum}_TECH_CONTACT_PHONE`, ''],
          [`VENUE_${venueNum}_ARTIST_LIAISON_NAME`, ''],
          [`VENUE_${venueNum}_ARTIST_LIAISON_EMAIL`, ''],
          [`VENUE_${venueNum}_ARTIST_LIAISON_PHONE`, ''],
          [`VENUE_${venueNum}_TRANSPORT_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_TRANSPORT_CONTACT_PHONE`, ''],
          [`VENUE_${venueNum}_HOTEL_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_HOTEL_CONTACT_PHONE`, ''],
          [`VENUE_${venueNum}_CATERING_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_CATERING_CONTACT_PHONE`, ''],
          [`VENUE_${venueNum}_SECURITY_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_SECURITY_CONTACT_PHONE`, ''],
          [`VENUE_${venueNum}_MERCHANDISING_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_MERCHANDISING_CONTACT_PHONE`, ''],
          [`VENUE_${venueNum}_PROMOTER_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_PROMOTER_CONTACT_EMAIL`, ''],
          [`VENUE_${venueNum}_PROMOTER_CONTACT_PHONE`, ''],
          [`VENUE_${venueNum}_PRODUCTION_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_PRODUCTION_CONTACT_PHONE`, ''],
          [`VENUE_${venueNum}_MARKETING_CONTACT_NAME`, ''],
          [`VENUE_${venueNum}_MARKETING_CONTACT_EMAIL`, ''],
          [`VENUE_${venueNum}_LUNCH_TYPE`, 'venue_catering'],
          [`VENUE_${venueNum}_LUNCH_LOCATION`, ''],
          [`VENUE_${venueNum}_LUNCH_BUDGET`, ''],
          [`VENUE_${venueNum}_DINNER_TYPE`, 'venue_catering'],
          [`VENUE_${venueNum}_DINNER_LOCATION`, ''],
          [`VENUE_${venueNum}_HOTEL_NAME`, ''],
          [`VENUE_${venueNum}_HOTEL_ADDRESS`, '']
        ];
      }).flat()
    ),
    
    // Transport routes between venues
    VENUE_1_TO_VENUE_2_TRANSPORT_TYPE: 'train',
    VENUE_1_TO_VENUE_2_DEPARTURE_TIME: '12:00',
    VENUE_1_TO_VENUE_2_ARRIVAL_TIME: '15:00',
    VENUE_1_TO_VENUE_2_DETAILS: '',
    VENUE_2_TO_VENUE_3_TRANSPORT_TYPE: 'private_car',
    VENUE_2_TO_VENUE_3_DEPARTURE_TIME: '12:00',
    VENUE_2_TO_VENUE_3_ARRIVAL_TIME: '15:00',
    VENUE_2_TO_VENUE_3_DETAILS: '',
    
    // Initialize remaining transport routes (3-14)
    ...Object.fromEntries(
      Array.from({ length: 11 }, (_, i) => {
        const fromVenue = i + 3;
        const toVenue = fromVenue + 1;
        return [
          [`VENUE_${fromVenue}_TO_VENUE_${toVenue}_TRANSPORT_TYPE`, 'private_car'],
          [`VENUE_${fromVenue}_TO_VENUE_${toVenue}_DEPARTURE_TIME`, '12:00'],
          [`VENUE_${fromVenue}_TO_VENUE_${toVenue}_ARRIVAL_TIME`, '15:00'],
          [`VENUE_${fromVenue}_TO_VENUE_${toVenue}_DETAILS`, '']
        ];
      }).flat()
    )
  } as TourVariables;
};

// Enhanced TemplateVariables interface
interface TemplateVariables {
  ARTIST: string;
  VENUE: string;
  VENUE_ADDRESS?: string;
  DATE?: string;
  CONTACT_NAME?: string;
  CONTACT_EMAIL?: string;
  CONTACT_PHONE?: string;
  
  // International variables
  LOCATIONA?: string;
  LOCATIONB?: string;
  LOCATIONC?: string;
  LOCATIOND?: string;
  INBOUNDAIRLINE?: string;
  INBOUND_FLIGHT_NO?: string;
  INBOUND_BOOKING_REF?: string;
  OUTBOUNDAIRLINE?: string;
  OUTBOUND_FLIGHT_NO?: string;
  OUTBOUND_BOOKING_REF?: string;
  INBOUNDTRAIN?: string;
  INBOUND_TRAIN_NO?: string;
  OUTBOUNDTRAIN?: string;
  OUTBOUND_TRAIN_NO?: string;
  INBOUND_BUS?: string;
  INBOUND_BUS_NO?: string;
  OUTBOUND_BUS?: string;
  OUTBOUND_BUS_NO?: string;
  INBOUND_CAR?: string;
  INBOUND_CAR_NO?: string;
  OUTBOUND_CAR?: string;
  OUTBOUND_CAR_NO?: string;
  HOTEL?: string;
  ARRIVAL_DATE?: string;
  DEPARTURE_DATE?: string;
  
  // Residency variables
  ARTISTIC_DIRECTOR?: string;
  TECH_MANAGER?: string;
  HOTEL_NAME?: string;
  HOTEL_ADDRESS?: string;
  RESIDENCY_DURATION?: string;
  LOCAL_ARTIST_1?: string;
  LOCAL_ARTIST_2?: string;
  LOCAL_ARTIST_3?: string;
  COMMUNITY_PARTNER_1?: string;
  COMMUNITY_PARTNER_2?: string;
  CULTURAL_SITE?: string;
  RESTAURANT?: string;
  
  [key: string]: string | boolean | any | undefined;
}

interface TourDay {
  date: Date;
  items: ScheduleItem[];
}

interface DatabaseScheduleState {
  scheduleId?: string;
  resourceId?: string;
  isLoadingFromDb: boolean;
  isSaving: boolean;
  saveStatus: 'saved' | 'saving' | 'error' | 'unsaved';
  lastSaved?: Date;
  version?: number;
}
const TourScheduler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { scheduleId, resourceId, performanceData } = location.state || {};

  // Database state
  const [dbState, setDbState] = useState<DatabaseScheduleState>({
    scheduleId,
    resourceId,
    isLoadingFromDb: false,
    isSaving: false,
    saveStatus: scheduleId ? 'saved' : 'unsaved'
  });

  // Core schedule state
  const [schedule, setSchedule] = useState<TourDay[]>([]);
  const [isInternational, setIsInternational] = useState(false);
  const [scheduleType, setScheduleType] = useState<'performance' | 'residency' | 'tour'>('performance');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  
  // Type filtering state
  const [typeFilters, setTypeFilters] = useState<Set<string>>(new Set());
  const performanceDate = performanceData?.timing?.date ? new Date(performanceData.timing.date) : new Date();

  // Calculate default arrival and departure
  const defaultArrival = new Date(performanceDate);
  defaultArrival.setDate(defaultArrival.getDate() - 1);
  
  const defaultDeparture = new Date(performanceDate);
  defaultDeparture.setDate(defaultDeparture.getDate() + 1);
  
  // Initialize variables with proper type safety
  const [variables, setVariables] = useState<ScheduleVariables>(() => {
    // Default to TemplateVariables for performance/residency
    const defaultTemplateVariables: TemplateVariables = {
      ARTIST: performanceData?.bandName || '',
      VENUE: performanceData?.venue?.name || '',
      VENUE_ADDRESS: performanceData?.venue?.location || '',
      DATE: performanceData?.timing?.date ? format(performanceData.timing.date, 'dd-MM-yyyy') : '',
      CONTACT_NAME: performanceData?.contactName || '',
      CONTACT_EMAIL: performanceData?.contactEmail || '',
      CONTACT_PHONE: performanceData?.contactPhone || '',
      LOCATIONA: '',
      LOCATIONB: '',
      LOCATIONC: '',
      LOCATIOND: '',
      INBOUNDAIRLINE: '',
      INBOUND_FLIGHT_NO: '',
      INBOUND_BOOKING_REF: '',
      OUTBOUNDAIRLINE: '',
      OUTBOUND_FLIGHT_NO: '',
      OUTBOUND_BOOKING_REF: '',
      INBOUNDTRAIN: '',
      INBOUND_TRAIN_NO: '',
      OUTBOUNDTRAIN: '',
      OUTBOUND_TRAIN_NO: '',
      INBOUND_BUS: '',
      INBOUND_BUS_NO: '',
      OUTBOUND_BUS: '',
      OUTBOUND_BUS_NO: '',
      INBOUND_CAR: '',
      INBOUND_CAR_NO: '',
      OUTBOUND_CAR: '',
      OUTBOUND_CAR_NO: '',
      HOTEL: '',
      ARRIVAL_DATE: format(defaultArrival, 'yyyy-MM-dd'),
      DEPARTURE_DATE: format(defaultDeparture, 'yyyy-MM-dd'),
      ARTISTIC_DIRECTOR: '',
      TECH_MANAGER: '',
      HOTEL_NAME: '',
      HOTEL_ADDRESS: '',
      RESIDENCY_DURATION: '3',
      LOCAL_ARTIST_1: '',
      LOCAL_ARTIST_2: '',
      LOCAL_ARTIST_3: '',
      COMMUNITY_PARTNER_1: '',
      COMMUNITY_PARTNER_2: '',
      CULTURAL_SITE: '',
      RESTAURANT: ''
    };
    
    return defaultTemplateVariables;
  });

  // Type-safe variable change handler
  const handleVariableChange = (key: string, value: any) => {
    setVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Auto-apply variables when tour variables change
  useEffect(() => {
    if (scheduleType === 'tour' && schedule.length > 0 && isTourVariables(variables, scheduleType)) {
      const newSchedule = [...schedule];
      
      newSchedule.forEach(day => {
        day.items.forEach(item => {
          if (item.template) {
            let updatedDescription = item.template;
            Object.entries(variables).forEach(([key, value]) => {
              const placeholder = `{${key}}`;
              updatedDescription = updatedDescription.replace(new RegExp(placeholder, 'g'), String(value || ''));
            });
            item.description = updatedDescription;
          }
        });
      });
      
      setSchedule(newSchedule);
    }
  }, [
    scheduleType,
    variables.VENUE_1_NAME, variables.VENUE_2_NAME, variables.VENUE_3_NAME,
    variables.VENUE_1_CITY, variables.VENUE_2_CITY, variables.VENUE_3_CITY,
    variables.VENUE_1_HOTEL_NAME, variables.VENUE_2_HOTEL_NAME, variables.VENUE_3_HOTEL_NAME,
    variables.VENUE_1_TECH_CONTACT_NAME, variables.VENUE_2_TECH_CONTACT_NAME, variables.VENUE_3_TECH_CONTACT_NAME,
    variables.VENUE_1_TO_VENUE_2_TRANSPORT_TYPE, variables.VENUE_2_TO_VENUE_3_TRANSPORT_TYPE
  ]);

  // Auto-generate tour schedule when key variables change
  useEffect(() => {
    if (scheduleType === 'tour' && !scheduleId && isTourVariables(variables, scheduleType)) {
      const numberOfShows = parseInt(variables.NUMBER_OF_SHOWS) || 0;
      
      if (numberOfShows > 0 && numberOfShows <= 14) {
        console.log(`🎸 Auto-generating tour for ${numberOfShows} shows`);
        
        try {
          const tourDays = getTourTemplate(variables);
          
          if (tourDays && tourDays.length > 0) {
            console.log(`✅ Generated ${tourDays.length} tour days`);
            setSchedule(tourDays.map(tourDay => ({
              date: tourDay.date,
              items: tourDay.items
            })));
          }
        } catch (error) {
          console.error('❌ Error auto-generating tour schedule:', error);
        }
      }
    }
  }, [scheduleType, variables.NUMBER_OF_SHOWS, variables.TOUR_START_DATE]);

  // Proper schedule type change handler
  const handleScheduleTypeChange = (newScheduleType: 'performance' | 'residency' | 'tour') => {
    const performanceDate = performanceData?.timing?.date ? new Date(performanceData.timing.date) : new Date();
    
    setScheduleType(newScheduleType);
    
    if (newScheduleType === 'tour') {
      // Switch to TourVariables
      const tourVariables = createDefaultTourVariables(performanceData, performanceDate);
      setVariables(tourVariables);
      
      // Generate initial tour schedule
      try {
        const tourDays = getTourTemplate(tourVariables);
        if (tourDays && tourDays.length > 0) {
          setSchedule(tourDays.map(tourDay => ({
            date: tourDay.date,
            items: tourDay.items
          })));
        } else {
          setSchedule([]);
        }
      } catch (error) {
        console.error('Error generating tour template:', error);
        setSchedule([]);
      }
    } else if (newScheduleType === 'residency') {
      // Switch back to TemplateVariables for residency
      const templateVars: TemplateVariables = {
        ARTIST: performanceData?.bandName || '',
        VENUE: performanceData?.venue?.name || '',
        VENUE_ADDRESS: performanceData?.venue?.location || '',
        DATE: performanceData?.timing?.date ? format(performanceData.timing.date, 'dd-MM-yyyy') : '',
        CONTACT_NAME: performanceData?.contactName || '',
        CONTACT_EMAIL: performanceData?.contactEmail || '',
        CONTACT_PHONE: performanceData?.contactPhone || '',
        ARTISTIC_DIRECTOR: '',
        TECH_MANAGER: '',
        HOTEL_NAME: '',
        HOTEL_ADDRESS: '',
        RESIDENCY_DURATION: '3',
        LOCAL_ARTIST_1: '',
        LOCAL_ARTIST_2: '',
        LOCAL_ARTIST_3: '',
        COMMUNITY_PARTNER_1: '',
        COMMUNITY_PARTNER_2: '',
        CULTURAL_SITE: '',
        RESTAURANT: ''
      };
      setVariables(templateVars);
      
      const residencyTemplate = getNavigatorResidencyTemplate(templateVars);
      setSchedule(residencyTemplate.days.map(day => ({
        date: addDays(performanceDate, day.dayNumber - 1),
        items: day.items
      })));
    } else {
      // Performance type - switch back to TemplateVariables
      const templateVars: TemplateVariables = {
        ARTIST: performanceData?.bandName || '',
        VENUE: performanceData?.venue?.name || '',
        VENUE_ADDRESS: performanceData?.venue?.location || '',
        DATE: performanceData?.timing?.date ? format(performanceData.timing.date, 'dd-MM-yyyy') : '',
        CONTACT_NAME: performanceData?.contactName || '',
        CONTACT_EMAIL: performanceData?.contactEmail || '',
        CONTACT_PHONE: performanceData?.contactPhone || '',
        LOCATIONA: '',
        LOCATIONB: '',
        LOCATIONC: '',
        LOCATIOND: '',
        INBOUNDAIRLINE: '',
        INBOUND_FLIGHT_NO: '',
        INBOUND_BOOKING_REF: '',
        OUTBOUNDAIRLINE: '',
        OUTBOUND_FLIGHT_NO: '',
        OUTBOUND_BOOKING_REF: '',
        INBOUNDTRAIN: '',
        INBOUND_TRAIN_NO: '',
        OUTBOUNDTRAIN: '',
        OUTBOUND_TRAIN_NO: '',
        INBOUND_BUS: '',
        INBOUND_BUS_NO: '',
        OUTBOUND_BUS: '',
        OUTBOUND_BUS_NO: '',
        INBOUND_CAR: '',
        INBOUND_CAR_NO: '',
        OUTBOUND_CAR: '',
        OUTBOUND_CAR_NO: '',
        HOTEL: '',
        ARRIVAL_DATE: format(new Date(performanceDate.getTime() - 24*60*60*1000), 'yyyy-MM-dd'),
        DEPARTURE_DATE: format(new Date(performanceDate.getTime() + 24*60*60*1000), 'yyyy-MM-dd')
      };
      setVariables(templateVars);
      
      const template = isInternational ? 
        getInternationalTemplate(templateVars) : 
        performanceData?.venue?.name === 'The Cooler' ?
          getCoolerTemplate(templateVars) :
          getDefaultVenueTemplate(templateVars);
      setSchedule([{
        date: performanceDate,
        items: template
      }]);
    }
  };

  // Load from database or initialize new
  useEffect(() => {
    console.log('🔄 useEffect triggered', { scheduleId, hasScheduleId: !!scheduleId });
    
    if (scheduleId) {
      console.log('📥 Loading existing schedule from database');
      loadScheduleFromDatabase();
    } else {
      console.log('🆕 Initializing new schedule');
      initializeNewSchedule();
    }
  }, [scheduleId]);

  const loadScheduleFromDatabase = async () => {
    try {
      console.log('🔄 Starting to load schedule from database, scheduleId:', scheduleId);
      setDbState((prev: DatabaseScheduleState) => ({ ...prev, isLoadingFromDb: true }));
      
      const response = await axiosInstance.get(`/api/schedules/${scheduleId}`);
      const dbSchedule = response.data;
      
      if (!dbSchedule || !dbSchedule.scheduleData) {
        throw new Error('Invalid schedule data structure received from database');
      }
      
      // Determine the schedule type first
      const dbScheduleType = dbSchedule.scheduleData.scheduleType === 'residency' ? 'residency' :
                            dbSchedule.scheduleData.scheduleType === 'tour' ? 'tour' : 
                            'performance';
      
      // Set schedule type
      setScheduleType(dbScheduleType);
      setIsInternational(dbSchedule.scheduleData.isInternational);
      
      // Handle variables based on type
      if (dbScheduleType === 'tour') {
        // Use TourVariables with complete defaults
        const defaultTourVars = createDefaultTourVariables(performanceData, performanceDate);
        const mergedTourVariables = { 
          ...defaultTourVars, 
          ...dbSchedule.scheduleData.variables 
        };
        setVariables(mergedTourVariables);
      } else {
        // Use TemplateVariables
        const defaultTemplateVars: TemplateVariables = {
          ARTIST: '',
          VENUE: '',
          VENUE_ADDRESS: '',
          DATE: '',
          CONTACT_NAME: '',
          CONTACT_EMAIL: '',
          CONTACT_PHONE: '',
          LOCATIONA: '',
          LOCATIONB: '',
          LOCATIONC: '',
          LOCATIOND: '',
          INBOUNDAIRLINE: '',
          INBOUND_FLIGHT_NO: '',
          INBOUND_BOOKING_REF: '',
          OUTBOUNDAIRLINE: '',
          OUTBOUND_FLIGHT_NO: '',
          OUTBOUND_BOOKING_REF: '',
          INBOUNDTRAIN: '',
          INBOUND_TRAIN_NO: '',
          OUTBOUNDTRAIN: '',
          OUTBOUND_TRAIN_NO: '',
          INBOUND_BUS: '',
          INBOUND_BUS_NO: '',
          OUTBOUND_BUS: '',
          OUTBOUND_BUS_NO: '',
          INBOUND_CAR: '',
          INBOUND_CAR_NO: '',
          OUTBOUND_CAR: '',
          OUTBOUND_CAR_NO: '',
          HOTEL: '',
          ARRIVAL_DATE: '',
          DEPARTURE_DATE: '',
          ARTISTIC_DIRECTOR: '',
          TECH_MANAGER: '',
          HOTEL_NAME: '',
          HOTEL_ADDRESS: '',
          RESIDENCY_DURATION: '3',
          LOCAL_ARTIST_1: '',
          LOCAL_ARTIST_2: '',
          LOCAL_ARTIST_3: '',
          COMMUNITY_PARTNER_1: '',
          COMMUNITY_PARTNER_2: '',
          CULTURAL_SITE: '',
          RESTAURANT: ''
        };
        
        const mergedTemplateVariables = { 
          ...defaultTemplateVars, 
          ...dbSchedule.scheduleData.variables 
        };
        setVariables(mergedTemplateVariables);
      }
      
      // Set schedule
      const scheduleWithDates = dbSchedule.scheduleData.schedule.map((day: any) => ({
        ...day,
        date: new Date(day.date)
      }));
      setSchedule(scheduleWithDates);
      
      setDbState((prev: DatabaseScheduleState) => ({
        ...prev,
        version: dbSchedule.version,
        lastSaved: new Date(dbSchedule.updatedAt),
        saveStatus: 'saved'
      }));
      
      console.log('✅ Schedule loaded from database successfully');
      
    } catch (error: any) {
      console.error('❌ Error loading schedule from database:', error);
      
      if (error.response?.status === 404) {
        alert('Schedule not found. It may have been deleted.');
        navigate(-1);
        return;
      }
      
      if (error.response?.status === 400) {
        alert('Invalid schedule ID format.');
        navigate(-1);
        return;
      }
      
      setDbState((prev: DatabaseScheduleState) => ({ ...prev, saveStatus: 'error' }));
      
      try {
        initializeNewSchedule();
      } catch (initError) {
        console.error('❌ Error initializing new schedule:', initError);
      }
      
    } finally {
      setDbState((prev: DatabaseScheduleState) => ({ ...prev, isLoadingFromDb: false }));
    }
  };

  const initializeNewSchedule = () => {
    if (scheduleId) {
      console.log('⚠️ Skipping initializeNewSchedule - schedule should be loaded from database');
      return;
    }
    
    if (performanceData?.timing?.date) {
      const detectedScheduleType = performanceData.venue?.name?.toLowerCase().includes('navigator') ? 'residency' : 'performance';
      setScheduleType(detectedScheduleType);
      
      const performanceDate = new Date(performanceData.timing.date);
      
      if (detectedScheduleType === 'residency') {
        const residencyTemplate = getNavigatorResidencyTemplate(variables as TemplateVariables);
        setSchedule(residencyTemplate.days.map(day => ({
          date: addDays(performanceDate, day.dayNumber - 1),
          items: day.items
        })));
      } else {
        const template = isInternational ? 
          getInternationalTemplate(variables as TemplateVariables) : 
          performanceData.venue?.name === 'The Cooler' ?
            getCoolerTemplate(variables as TemplateVariables) :
            getDefaultVenueTemplate(variables as TemplateVariables);
        setSchedule([{
          date: performanceDate,
          items: template
        }]);
      }
    }
  };
  // Save to database
  const saveToDatabase = async () => {
    if (!scheduleId) return;
    
    try {
      setDbState((prev: DatabaseScheduleState) => ({ ...prev, isSaving: true, saveStatus: 'saving' }));
      
      // Filter out empty items before saving
      const cleanedSchedule = schedule.map(day => ({
        ...day,
        items: day.items.filter(item => 
          item.time && item.time.trim() !== '' && 
          item.description && item.description.trim() !== ''
        )
      }));
      
      const response = await axiosInstance.put(`/api/schedules/${scheduleId}`, {
        scheduleData: {
          variables,
          schedule: cleanedSchedule,
          scheduleType,
          isInternational
        }
      });
      
      setDbState((prev: DatabaseScheduleState) => ({
        ...prev,
        saveStatus: 'saved',
        lastSaved: new Date(),
        version: response.data.version
      }));
      
      console.log('✅ Schedule saved to database');
      
    } catch (error: any) {
      console.error('❌ Error saving schedule:', error);
      setDbState((prev: DatabaseScheduleState) => ({ ...prev, saveStatus: 'error' }));
      
      if (error.response?.status === 409) {
        const shouldReload = window.confirm(
          'This schedule has been updated by another user. Do you want to reload the latest version? (Your changes will be lost)'
        );
        if (shouldReload) {
          await loadScheduleFromDatabase();
        }
      }
    } finally {
      setDbState((prev: DatabaseScheduleState) => ({ ...prev, isSaving: false }));
    }
  };

  // Auto-save when schedule changes - FIXED VERSION
  useEffect(() => {
    if (dbState.isSaving || dbState.isLoadingFromDb) {
      return;
    }
    
    if (scheduleId && (schedule.length > 0 || Object.keys(variables).some(key => (variables as any)[key]))) {
      setDbState((prev: DatabaseScheduleState) => ({ ...prev, saveStatus: 'unsaved' }));
      
      const timeoutId = setTimeout(() => {
        saveToDatabase();
      }, 3000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [schedule, variables, scheduleType, isInternational]);

  // Get event types based on schedule type
  const getEventTypes = () => {
    if (scheduleType === 'tour') {
      return getTourEventTypes();
    }
    return scheduleType === 'residency' ? 
      { ...EVENT_TYPES, ...RESIDENCY_EVENT_TYPES } : 
      EVENT_TYPES;
  };

  // Add this new function to insert an item after a specific index
  const addScheduleItemAfter = (dayIndex: number, afterItemIndex: number) => {
    const newItem: ScheduleItem = {
      id: Math.random().toString(36).substr(2, 9),
      time: '',
      type: 'OTHER',
      description: ''
    };

    const newSchedule = [...schedule];
    // Insert the new item at the position after the current item
    newSchedule[dayIndex].items.splice(afterItemIndex + 1, 0, newItem);
    setSchedule(newSchedule);
  };

  // Filter event types based on selected filters
  const getFilteredEventTypes = () => {
    const eventTypes = getEventTypes();
    if (typeFilters.size === 0) return eventTypes;
    
    const filtered: { [key: string]: string } = {};
    Object.entries(eventTypes).forEach(([key, value]) => {
      if (!typeFilters.has(key)) {
        filtered[key] = value;
      }
    });
    return filtered;
  };

  // Toggle type filter
  const toggleTypeFilter = (type: string) => {
    const newFilters = new Set(typeFilters);
    if (newFilters.has(type)) {
      newFilters.delete(type);
    } else {
      newFilters.add(type);
    }
    setTypeFilters(newFilters);
  };

  // Reset all filters
  const resetTypeFilters = () => {
    setTypeFilters(new Set());
  };

  // Get all available types for filtering
  const getAllEventTypes = () => {
    return getEventTypes();
  };

  // Check if a schedule item should be visible based on filters
  const isItemVisible = (item: ScheduleItem) => {
    if (typeFilters.size === 0) return true;
    return !typeFilters.has(item.type);
  };

  // Get excluded type names for display
  const getExcludedTypeNames = () => {
    const eventTypes = getAllEventTypes();
    return Array.from(typeFilters)
      .map(type => eventTypes[type as keyof typeof eventTypes])
      .filter(Boolean);
  };

  // Updated variable display logic with enhanced international fields
  const shouldShowVariable = (key: string): boolean => {
    const isInternationalField = [
      'LOCATIONA', 'LOCATIONB', 'LOCATIONC', 'LOCATIOND',
      'INBOUNDAIRLINE', 'INBOUND_FLIGHT_NO', 'INBOUND_BOOKING_REF',
      'OUTBOUNDAIRLINE', 'OUTBOUND_FLIGHT_NO', 'OUTBOUND_BOOKING_REF',
      'INBOUNDTRAIN', 'INBOUND_TRAIN_NO', 'OUTBOUNDTRAIN', 'OUTBOUND_TRAIN_NO',
      'INBOUND_BUS', 'INBOUND_BUS_NO', 'OUTBOUND_BUS', 'OUTBOUND_BUS_NO',
      'INBOUND_CAR', 'INBOUND_CAR_NO', 'OUTBOUND_CAR', 'OUTBOUND_CAR_NO',
      'HOTEL', 'ARRIVAL_DATE', 'DEPARTURE_DATE'
    ].includes(key);
    
    const isResidencyField = [
      'ARTISTIC_DIRECTOR', 'TECH_MANAGER', 'HOTEL_NAME', 'HOTEL_ADDRESS', 
      'RESIDENCY_DURATION', 'LOCAL_ARTIST_1', 'LOCAL_ARTIST_2', 'LOCAL_ARTIST_3', 
      'COMMUNITY_PARTNER_1', 'COMMUNITY_PARTNER_2', 'CULTURAL_SITE', 'RESTAURANT'
    ].includes(key);
    
    if (isInternationalField && !isInternational) return false;
    if (isResidencyField && scheduleType !== 'residency') return false;
    
    return true;
  };

  // Updated field labels with enhanced international fields
  const getFieldLabel = (key: string): string => {
    const labels: { [key: string]: string } = {
      ARTIST: 'Artist Name',
      VENUE: 'Venue Name',
      VENUE_ADDRESS: 'Venue Address',
      DATE: 'Date',
      CONTACT_NAME: 'Contact Name',
      CONTACT_EMAIL: 'Contact Email',
      CONTACT_PHONE: 'Contact Phone',
      ARRIVAL_DATE: 'Arrival Date (if different from performance)',
      DEPARTURE_DATE: 'Departure Date (if different from performance)',
      
      // Location fields
      LOCATIONA: 'Origin Location',
      LOCATIONB: 'Performance Location',
      LOCATIONC: 'Next Destination',
      LOCATIOND: 'Additional Location',
      
      // Inbound travel
      INBOUNDAIRLINE: 'Inbound Airline',
      INBOUND_FLIGHT_NO: 'Inbound Flight Number',
      INBOUND_BOOKING_REF: 'Inbound Booking Reference',
      INBOUNDTRAIN: 'Inbound Train Company',
      INBOUND_TRAIN_NO: 'Inbound Train Number',
      INBOUND_BUS: 'Inbound Bus Company',
      INBOUND_BUS_NO: 'Inbound Bus Number',
      INBOUND_CAR: 'Inbound Car/Transfer',
      INBOUND_CAR_NO: 'Inbound Vehicle Details',
      
      // Outbound travel
      OUTBOUNDAIRLINE: 'Outbound Airline',
      OUTBOUND_FLIGHT_NO: 'Outbound Flight Number',
      OUTBOUND_BOOKING_REF: 'Outbound Booking Reference',
      OUTBOUNDTRAIN: 'Outbound Train Company',
      OUTBOUND_TRAIN_NO: 'Outbound Train Number',
      OUTBOUND_BUS: 'Outbound Bus Company',
      OUTBOUND_BUS_NO: 'Outbound Bus Number',
      OUTBOUND_CAR: 'Outbound Car/Transfer',
      OUTBOUND_CAR_NO: 'Outbound Vehicle Details',
      
      HOTEL: 'Hotel Name',
      
      // Residency fields
      ARTISTIC_DIRECTOR: 'Artistic Director',
      TECH_MANAGER: 'Technical Manager',
      HOTEL_NAME: 'Hotel Name',
      HOTEL_ADDRESS: 'Hotel Address',
      RESIDENCY_DURATION: 'Residency Duration (days)',
      LOCAL_ARTIST_1: 'Local Artist 1',
      LOCAL_ARTIST_2: 'Local Artist 2',
      LOCAL_ARTIST_3: 'Local Artist 3',
      COMMUNITY_PARTNER_1: 'Community Partner 1',
      COMMUNITY_PARTNER_2: 'Community Partner 2',
      CULTURAL_SITE: 'Cultural Site',
      RESTAURANT: 'Restaurant'
    };
    return labels[key] || key.replace(/_/g, ' ');
  };

  // Enhanced international toggle handler
  const handleInternationalToggle = (newIsInternational: boolean) => {
    setIsInternational(newIsInternational);
    
    if (newIsInternational) {
      const performanceDate = schedule.find(day => day.items.some(item => item.type === 'PERFORMANCE'))?.date || schedule[0]?.date || new Date();
      
      console.log('🔍 Debug getSmartInternationalSchedule:');
      console.log('variables.ARRIVAL_DATE:', variables.ARRIVAL_DATE);
      console.log('variables.DEPARTURE_DATE:', variables.DEPARTURE_DATE);
      console.log('performanceDate:', performanceDate);
      
      const newSchedule = getSmartInternationalSchedule(variables as TemplateVariables, performanceDate);
      console.log('🔍 Generated schedule:', newSchedule);
      console.log('🔍 Number of days:', newSchedule.length);
      
      const shouldReplace = window.confirm(
        'Do you want to replace the current schedule with the international multi-day template?'
      );
      
      if (shouldReplace) {
        setSchedule(newSchedule);
      }
    } else {
      // When switching FROM international, revert to single-day venue template
      const performanceDate = schedule.find(day => day.items.some(item => item.type === 'PERFORMANCE'))?.date || schedule[0]?.date || new Date();
      
      const template = performanceData?.venue?.name === 'The Cooler' ?
        getCoolerTemplate(variables as TemplateVariables) :
        getDefaultVenueTemplate(variables as TemplateVariables);
        
      const newSchedule = [{
        date: performanceDate,
        items: template
      }];
      
      const shouldReplace = window.confirm(
        'Do you want to replace the current schedule with the single-day venue template?'
      );
      
      if (shouldReplace) {
        setSchedule(newSchedule);
      }
    }
  };

  // Schedule manipulation functions - FIXED TO USE PERFORMANCE DATE
  const addDay = () => {
    let newDate: Date;
    
    if (schedule.length === 0) {
      // If no days exist, use the performance date
      if (performanceData?.timing?.date) {
        newDate = new Date(performanceData.timing.date);
      } else {
        newDate = new Date();
      }
    } else {
      // Add one day after the last day
      const lastDay = schedule[schedule.length - 1];
      newDate = addDays(lastDay.date, 1);
    }
    
    let template: ScheduleItem[];
    
    if (scheduleType === 'residency') {
      template = [
        {
          id: Math.random().toString(36).substr(2, 9),
          time: '09:00',
          type: 'VENUE',
          description: `${variables.ARTIST || ''} workspace time`,
          template: '{ARTIST} workspace time'
        },
        {
          id: Math.random().toString(36).substr(2, 9),
          time: '12:00',
          type: 'CATERING',
          description: 'Lunch break'
        }
      ];
    } else {
      template = isInternational ? 
        getInternationalTemplate(variables as TemplateVariables) : 
        performanceData?.venue?.name === 'The Cooler' ?
          getCoolerTemplate(variables as TemplateVariables) :
          getDefaultVenueTemplate(variables as TemplateVariables);
    }
    
    setSchedule([...schedule, {
      date: newDate,
      items: template
    }]);
  };

  const removeDay = (index: number) => {
    setSchedule(schedule.filter((_, i) => i !== index));
  };

  // Update the existing addScheduleItem function (keep this for the "Add Item" button at the top)
  const addScheduleItem = (dayIndex: number) => {
    const newItem: ScheduleItem = {
      id: Math.random().toString(36).substr(2, 9),
      time: '',
      type: 'OTHER',
      description: ''
    };

    const newSchedule = [...schedule];
    newSchedule[dayIndex].items.push(newItem);
    setSchedule(newSchedule);
  };

  const updateScheduleItem = (dayIndex: number, itemIndex: number, field: keyof ScheduleItem, value: string) => {
    const newSchedule = [...schedule];
    const item = newSchedule[dayIndex].items[itemIndex];
    
    if (field === 'time') {
      item.time = value;
    } else if (field === 'type') {
      item.type = value as keyof typeof EVENT_TYPES | keyof typeof RESIDENCY_EVENT_TYPES;
    } else if (field === 'description') {
      item.description = value;
    }
    
    setSchedule(newSchedule);
  };

  const removeScheduleItem = (dayIndex: number, itemIndex: number) => {
    const newSchedule = [...schedule];
    newSchedule[dayIndex].items.splice(itemIndex, 1);
    setSchedule(newSchedule);
  };

  // Apply variables to templates
  const handleApplyVariables = () => {
    const newSchedule = [...schedule];
    
    newSchedule.forEach(day => {
      day.items.forEach(item => {
        if (item.template) {
          let updatedDescription = item.template;
          Object.entries(variables).forEach(([key, value]) => {
            const placeholder = `{${key}}`;
            updatedDescription = updatedDescription.replace(new RegExp(placeholder, 'g'), String(value || ''));
          });
          item.description = updatedDescription;
        }
      });
    });
    
    setSchedule(newSchedule);
  };
  // Export functions
  const exportSchedule = async () => {
    try {
      if (scheduleId && dbState.saveStatus === 'unsaved') {
        await saveToDatabase();
      }
      
      if (scheduleId) {
        setDbState((prev: DatabaseScheduleState) => ({ ...prev, isSaving: true }));
        
        const response = await axiosInstance.post(`/api/schedules/${scheduleId}/generate-pdf`);
        
        console.log('Full server response:', response.data);
        
        // Server returns schedule data, not a PDF URL - generate PDF on frontend
        if (response.data.schedule) {
          const scheduleData = response.data.schedule.map((day: any) => ({
            ...day,
            date: new Date(day.date)
          }));
          
          await exportScheduleToPDF(scheduleData, {
            title: `${response.data.scheduleType === 'residency' ? 'Residency' : response.data.scheduleType === 'tour' ? 'Tour' : 'Performance'} Schedule - ${response.data.variables.ARTIST}`,
            artist: response.data.variables.ARTIST,
            venue: response.data.variables.VENUE,
            venueAddress: response.data.variables.VENUE_ADDRESS,
            contactInfo: {
              name: response.data.variables.CONTACT_NAME || '',
              email: response.data.variables.CONTACT_EMAIL || '',
              phone: response.data.variables.CONTACT_PHONE || ''
            },
            logos: {
              primary: `https://storage.googleapis.com/showrunnr-bucket/logos/imc-logo.png`,
              secondary: `https://storage.googleapis.com/showrunnr-bucket/logos/venue-logo.png`
            }
          });
        } else {
          alert('No schedule data received from server');
        }
        
        setDbState((prev: DatabaseScheduleState) => ({ ...prev, isSaving: false }));
      } else {
        const filteredSchedule = schedule.map(day => ({
          ...day,
          items: day.items.filter(isItemVisible)
        }));

        // Use await for async PDF generation
        await exportScheduleToPDF(filteredSchedule, {
          title: `${scheduleType === 'residency' ? 'Residency' : scheduleType === 'tour' ? 'Tour' : 'Performance'} Schedule - ${variables.ARTIST}`,
          artist: variables.ARTIST,
          venue: variables.VENUE,
          venueAddress: variables.VENUE_ADDRESS,
          contactInfo: {
            name: variables.CONTACT_NAME || '',
            email: variables.CONTACT_EMAIL || '',
            phone: variables.CONTACT_PHONE || ''
          },
          logos: {
            primary: `https://storage.googleapis.com/showrunnr-bucket/logos/imc-logo.png`,
            secondary: `https://storage.googleapis.com/showrunnr-bucket/logos/venue-logo.png`
          }
        });
      }
    } catch (error: any) {
      console.error('Error exporting schedule:', error);
      alert('Error generating PDF. Please try again.');
      setDbState((prev: DatabaseScheduleState) => ({ ...prev, isSaving: false }));
    }
  };

  const exportToWord = async () => {
    try {
      const filteredSchedule = schedule.map(day => ({
        ...day,
        items: day.items.filter(isItemVisible)
      }));

      // Use await for async Word generation
      await exportScheduleToWord(filteredSchedule, {
        artist: variables.ARTIST,
        venue: variables.VENUE
      });
    } catch (error: any) {
      console.error('Error exporting to Word:', error);
      alert('Error generating Word document. Please try again.');
    }
  };

  // Save status indicator component
  const SaveStatusIndicator = () => {
    if (!scheduleId) return null;

    const getStatusIcon = () => {
      switch (dbState.saveStatus) {
        case 'saved':
          return <CheckCircle sx={{ color: 'green', fontSize: 16 }} />;
        case 'saving':
          return <CircularProgress size={16} />;
        case 'error':
          return <ErrorIcon sx={{ color: 'red', fontSize: 16 }} />;
        case 'unsaved':
          return <CloudSync sx={{ color: 'orange', fontSize: 16 }} />;
        default:
          return null;
      }
    };

    const getStatusText = () => {
      switch (dbState.saveStatus) {
        case 'saved':
          return dbState.lastSaved ? `Saved ${format(dbState.lastSaved, 'HH:mm')}` : 'Saved';
        case 'saving':
          return 'Saving...';
        case 'error':
          return 'Save failed';
        case 'unsaved':
          return 'Unsaved changes';
        default:
          return '';
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {getStatusIcon()}
        <Typography variant="caption" sx={{ fontSize: 12 }}>
          {getStatusText()}
        </Typography>
      </Box>
    );
  };

  if (dbState.isLoadingFromDb) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading schedule...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Database status banner */}
      {scheduleId && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success" sx={{ mb: 1 }}>
            Connected to database - changes auto-save every 3 seconds
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={`Schedule ID: ${scheduleId.slice(-8)}`} size="small" variant="outlined" />
            {dbState.version && <Chip label={`Version: ${dbState.version}`} size="small" variant="outlined" />}
            <SaveStatusIndicator />
          </Box>
        </Box>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowLeft />
          </IconButton>
          <Typography variant="h4">
            {scheduleType === 'residency' ? 'Residency' : 
             scheduleType === 'tour' ? 'Tour' : 'Performance'} Schedule
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SaveStatusIndicator />
          
          {scheduleId && (
            <Button
              variant="outlined"
              startIcon={dbState.isSaving ? <CircularProgress size={16} /> : <Save />}
              onClick={saveToDatabase}
              disabled={dbState.isSaving}
            >
              {dbState.isSaving ? 'Saving...' : 'Save Now'}
            </Button>
          )}
          
          <FormControl size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={scheduleType}
              label="Type"
              onChange={(e) => handleScheduleTypeChange(e.target.value as 'performance' | 'residency' | 'tour')}
            >
              <MenuItem value="performance">Performance</MenuItem>
              <MenuItem value="residency">Residency</MenuItem>
              <MenuItem value="tour">Tour</MenuItem>
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={isInternational}
                onChange={(e) => handleInternationalToggle(e.target.checked)}
              />
            }
            label="International"
          />
        </Box>
      </Box>

      {/* Variables Accordion - CLOSED BY DEFAULT */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Variables (click to edit if needed)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleApplyVariables}
              startIcon={<RefreshCw />}
            >
              Apply Variables to the Schedule
            </Button>
          </Box>

          {/* Tour Variables Section - Only when Tour */}
          {scheduleType === 'tour' ? (
            <TourVariablesSection
              variables={variables as TourVariables}
              onVariableChange={handleVariableChange}
              errors={{}}
              scheduleId={scheduleId}
              dbState={dbState}
              isInternational={isInternational}
              onInternationalChange={handleInternationalToggle}
            />
          ) : (
            <>
              {/* Core Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person /> Core Information
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(variables)
                    .filter(([key]) => ['ARTIST', 'VENUE', 'VENUE_ADDRESS', 'DATE', 'CONTACT_NAME', 'CONTACT_EMAIL', 'CONTACT_PHONE'].includes(key))
                    .map(([key, value]) => (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <TextField
                          label={getFieldLabel(key)}
                          value={value || ''}
                          onChange={(e) => handleVariableChange(key, e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          type={key === 'ARRIVAL_DATE' || key === 'DEPARTURE_DATE' ? 'date' : 'text'}
                          InputLabelProps={key === 'ARRIVAL_DATE' || key === 'DEPARTURE_DATE' ? { shrink: true } : undefined}
                          sx={{
                            '& .MuiInputBase-input': {
                              color: !value ? 'text.disabled' : 'text.primary'
                            },
                            '& .MuiInputLabel-root': {
                              color: !value ? 'text.disabled' : undefined
                            }
                          }}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>

              {/* Travel Information - Only when International */}
              {isInternational && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FlightTakeoff /> Travel Information
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(variables)
                      .filter(([key]) => [
                        'LOCATIONA', 'LOCATIONB', 'LOCATIONC', 'LOCATIOND',
                        'INBOUNDAIRLINE', 'INBOUND_FLIGHT_NO', 'INBOUND_BOOKING_REF',
                        'OUTBOUNDAIRLINE', 'OUTBOUND_FLIGHT_NO', 'OUTBOUND_BOOKING_REF',
                        'INBOUNDTRAIN', 'INBOUND_TRAIN_NO', 'OUTBOUNDTRAIN', 'OUTBOUND_TRAIN_NO',
                        'INBOUND_BUS', 'INBOUND_BUS_NO', 'OUTBOUND_BUS', 'OUTBOUND_BUS_NO',
                        'INBOUND_CAR', 'INBOUND_CAR_NO', 'OUTBOUND_CAR', 'OUTBOUND_CAR_NO',
                        'ARRIVAL_DATE', 'DEPARTURE_DATE'
                      ].includes(key))
                      .map(([key, value]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <TextField
                            label={getFieldLabel(key)}
                            value={value || ''}
                            onChange={(e) => handleVariableChange(key, e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            type={key === 'ARRIVAL_DATE' || key === 'DEPARTURE_DATE' ? 'date' : 'text'}
                            InputLabelProps={key === 'ARRIVAL_DATE' || key === 'DEPARTURE_DATE' ? { shrink: true } : undefined}
                            sx={{
                              '& .MuiInputBase-input': {
                                color: !value ? 'text.disabled' : 'text.primary'
                              },
                              '& .MuiInputLabel-root': {
                                color: !value ? 'text.disabled' : undefined
                              }
                            }}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              )}

              {/* Accommodation - Only when International */}
              {isInternational && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Hotel /> Accommodation
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(variables)
                      .filter(([key]) => ['HOTEL'].includes(key))
                      .map(([key, value]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <TextField
                            label={getFieldLabel(key)}
                            value={value || ''}
                            onChange={(e) => handleVariableChange(key, e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiInputBase-input': {
                                color: !value ? 'text.disabled' : 'text.primary'
                              },
                              '& .MuiInputLabel-root': {
                                color: !value ? 'text.disabled' : undefined
                              }
                            }}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              )}

              {/* Residency Information - Only when Residency */}
              {scheduleType === 'residency' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Groups /> Residency Information
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(variables)
                      .filter(([key]) => [
                        'ARTISTIC_DIRECTOR', 'TECH_MANAGER', 'HOTEL_NAME', 'HOTEL_ADDRESS', 
                        'RESIDENCY_DURATION', 'LOCAL_ARTIST_1', 'LOCAL_ARTIST_2', 'LOCAL_ARTIST_3', 
                        'COMMUNITY_PARTNER_1', 'COMMUNITY_PARTNER_2', 'CULTURAL_SITE', 'RESTAURANT'
                      ].includes(key))
                      .map(([key, value]) => (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <TextField
                            label={getFieldLabel(key)}
                            value={value || ''}
                            onChange={(e) => handleVariableChange(key, e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiInputBase-input': {
                                color: !value ? 'text.disabled' : 'text.primary'
                              },
                              '& .MuiInputLabel-root': {
                                color: !value ? 'text.disabled' : undefined
                              }
                            }}
                          />
                        </Grid>
                      ))}
                  </Grid>
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Export buttons across the top */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Schedule ({schedule.length} day{schedule.length !== 1 ? 's' : ''})
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={exportSchedule}
            startIcon={<PictureAsPdf />}
            size="small"
            disabled={dbState.isSaving}
          >
            Export PDF
          </Button>
          
          <Button
            variant="outlined"
            onClick={exportToWord}
            startIcon={<Microsoft />}
            size="small"
          >
            Export Word
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => setEmailDialogOpen(true)}
            startIcon={<EmailTwoTone />}
            size="small"
          >
            Email
          </Button>
          
          <Button
            variant="contained"
            onClick={addDay}
            startIcon={<Plus />}
            size="small"
          >
            Add Day
          </Button>
        </Box>
      </Box>

      {/* Type Filters */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList />
            <Typography>
              Event Type Filters
              {typeFilters.size > 0 && (
                <Chip 
                  label={`${typeFilters.size} hidden`} 
                  size="small" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={resetTypeFilters}
              size="small"
              disabled={typeFilters.size === 0}
            >
              Show All Types
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(getAllEventTypes()).map(([type, label]) => (
              <Chip
                key={type}
                label={label}
                onClick={() => toggleTypeFilter(type)}
                color={typeFilters.has(type) ? "default" : "primary"}
                variant={typeFilters.has(type) ? "outlined" : "filled"}
                sx={{ 
                  opacity: typeFilters.has(type) ? 0.5 : 1,
                  textDecoration: typeFilters.has(type) ? 'line-through' : 'none'
                }}
              />
            ))}
          </Box>
          
          {typeFilters.size > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Hiding: {getExcludedTypeNames().join(', ')}
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* FULL WIDTH Schedule Days */}
      {schedule.map((day, dayIndex) => (
        <Paper key={dayIndex} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ color: '#FFD700' }}>
              Day {dayIndex + 1} - {format(day.date, 'EEEE, MMMM d, yyyy')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => addScheduleItem(dayIndex)}
                startIcon={<Plus />}
                size="small"
              >
                Add Item
              </Button>
              <Button
                variant="outlined"
                onClick={addDay}
                startIcon={<Plus />}
                size="small"
              >
                Add Day
              </Button>
              {schedule.length > 1 && (
                <IconButton
                  onClick={() => removeDay(dayIndex)}
                  color="error"
                  size="small"
                >
                  <Minus />
                </IconButton>
              )}
            </Box>
          </Box>

          {day.items.filter(isItemVisible).map((item, itemIndex) => {
            const actualItemIndex = day.items.findIndex(originalItem => originalItem.id === item.id);
            return (
              <Box key={item.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                <TextField
                  type="time"
                  value={item.time}
                  onChange={(e) => updateScheduleItem(dayIndex, actualItemIndex, 'time', e.target.value)}
                  size="small"
                  sx={{ width: 160 }}
                />
                
                <FormControl size="small" sx={{ width: 150 }}>
                  <Select
                    value={item.type}
                    onChange={(e) => updateScheduleItem(dayIndex, actualItemIndex, 'type', e.target.value)}
                  >
                    {Object.entries(getFilteredEventTypes()).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  value={item.description}
                  onChange={(e) => updateScheduleItem(dayIndex, actualItemIndex, 'description', e.target.value)}
                  placeholder="Description"
                  size="small"
                  fullWidth
                  multiline
                  maxRows={3}
                />
                
                <IconButton
                  onClick={() => addScheduleItemAfter(dayIndex, actualItemIndex)}
                  color="primary"
                  size="small"
                >
                  <Plus />
                </IconButton>
                
                <IconButton
                  onClick={() => removeScheduleItem(dayIndex, actualItemIndex)}
                  color="error"
                  size="small"
                >
                  <Minus />
                </IconButton>
              </Box>
            );
          })}

          {day.items.filter(isItemVisible).length === 0 && typeFilters.size > 0 && (
            <Alert severity="info">
              All items for this day are filtered out. Adjust filters to see items.
            </Alert>
          )}
        </Paper>
      ))}

      {/* Email Dialog */}
      <EmailScheduleDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        schedule={schedule.map(day => ({
          ...day,
          items: day.items.filter(isItemVisible)
        }))}
      variables={{
  ...variables,
  VENUE_ADDRESS: variables.VENUE_ADDRESS || '',
  DATE: variables.DATE || '',
  CONTACT_NAME: variables.CONTACT_NAME || '',
  CONTACT_EMAIL: variables.CONTACT_EMAIL || '',
  CONTACT_PHONE: variables.CONTACT_PHONE || ''
} as any}
      />
    </Box>
  );
};

export default TourScheduler;